import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { ReactComponent as ChevronDown } from '@rocco/icons/svg/arrow/arrow-chevron-down.svg';

import type { B2BAssets, PartnersSection } from '../types';

export interface B2BPartnersStripProps {
    section: PartnersSection;
    assets: B2BAssets;
}

const DEFAULT_VISIBLE_COUNT = 6;

/**
 * "Meet our partners" (Figma node 443:24116, 1920×832 frame).
 *
 * Absolute-positioned 1920-frame layout:
 * - Portrait image left of centre, vertically centred, overflows the frame
 *   top/bottom (h=849.912 inside an 832 frame, x=25 / w=595.847).
 * - Section label "Meet our partners" at x=660.28, y=83.35 (28px Neue Haas).
 * - Vertical name list at x=1119.05 starting y=68.35, width 753.48, font 80px
 *   Neue Haas, tracking −3.84px. Wide names wrap to two lines. The active
 *   partner is rendered in #181818; all others in #c4c4c4.
 * - Up / down chevrons aligned to the left edge of the name column at y=21.35
 *   and y=755.35.
 *
 * Interactivity layered on top of the pixel-pass layout:
 *   1. Hovering a name (or focusing it) temporarily makes it the active partner
 *      and swaps the displayed portrait. Leaving the list reverts to the latched
 *      `clickedIndex` (or `section.activeIndex`).
 *   2. Clicking a name latches it as the new active partner.
 *   3. Up / down chevrons translate the inner column by the measured offsetHeight
 *      of the next item, bounded so the last `visibleCount` items can be reached
 *      without scrolling past the end. Disabled chevrons render at `opacity-50`
 *      and ignore clicks.
 *
 * vw conversions assume the 1920 design frame: px / 19.2 = vw.
 */
export const B2BPartnersStrip = ({
    section,
    assets,
}: B2BPartnersStripProps) => {
    const { partners } = section;
    const total = partners.length;
    const visibleCount = Math.min(
        section.visibleCount ?? DEFAULT_VISIBLE_COUNT,
        total,
    );
    const initialIndex = Math.min(
        Math.max(section.activeIndex ?? 0, 0),
        Math.max(total - 1, 0),
    );

    // Mobile has no hover, so a tap must switch the active banner rather than
    // navigate; desktop keeps hover-to-preview + click-to-open-profile.
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);

    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [clickedIndex, setClickedIndex] = useState<number>(initialIndex);
    const [scrollIndex, setScrollIndex] = useState<number>(0);
    const [itemHeights, setItemHeights] = useState<number[]>(() =>
        Array.from({ length: total }, () => 0),
    );

    const itemRefs = useRef<Array<HTMLElement | null>>([]);

    // Measure each item's rendered height (some names wrap to two lines, so we
    // can't assume a constant row height). Re-measure on resize so vw font
    // sizing stays in sync.
    const measure = useCallback(() => {
        const heights = itemRefs.current.map((node) => node?.offsetHeight ?? 0);
        setItemHeights((prev) => {
            if (
                prev.length === heights.length &&
                prev.every((h, i) => h === heights[i])
            ) {
                return prev;
            }
            return heights;
        });
    }, []);

    useLayoutEffect(() => {
        measure();
    }, [measure, total]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onResize = () => measure();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [measure]);

    const activeIndex = hoveredIndex ?? clickedIndex;

    // Resolve a partner's portrait: loader-resolved profile banner →
    // per-partner asset → section fallback asset.
    const fallbackAssetKey = section.imageAssetKey;
    const resolvePartnerImage = (
        partner: PartnersSection['partners'][number],
    ): string | undefined =>
        partner.imageUrl ??
        (partner.imageAssetKey ? assets[partner.imageAssetKey] : undefined) ??
        (fallbackAssetKey ? assets[fallbackAssetKey] : undefined);

    // Translate offset: sum the measured heights of items before `scrollIndex`.
    const translateY = useMemo(() => {
        let offset = 0;
        for (let i = 0; i < scrollIndex; i++) {
            offset += itemHeights[i] ?? 0;
        }
        return offset;
    }, [itemHeights, scrollIndex]);

    // Visible-window height: sum of the next `visibleCount` items from
    // `scrollIndex`. When measurements aren't ready we render `auto`, which
    // shows the whole list (acceptable transient on first paint).
    const windowHeight = useMemo(() => {
        if (itemHeights.every((h) => h === 0)) return null;
        let sum = 0;
        for (let i = 0; i < visibleCount; i++) {
            sum += itemHeights[i] ?? 0;
        }
        return sum;
    }, [itemHeights, visibleCount]);

    const canScrollUp = scrollIndex > 0;
    const canScrollDown = scrollIndex < total - visibleCount;

    const onScrollUp = useCallback(() => {
        setScrollIndex((i) => Math.max(i - 1, 0));
    }, []);

    const onScrollDown = useCallback(() => {
        setScrollIndex((i) => Math.min(i + 1, total - visibleCount));
    }, [total, visibleCount]);

    const onListMouseLeave = useCallback(() => {
        setHoveredIndex(null);
    }, []);

    return (
        <section className="relative w-full bg-white text-[#181818] max-md:aspect-[768/427] md:aspect-[1920/832]">
            {/* Portrait image.
             *  - Desktop: x=25, y=center, w=595.847, h=849.912.
             *  - Mobile (Figma `521:26866`): x=18, w=239, h=342 → 2.34 % left,
             *    31.1 % wide, vertically centred. Aspect comes out close to the
             *    desktop value so we keep the same aspect-ratio class. */}
            {partners.length > 0 && (
                <div className="absolute top-1/2 aspect-[595.847/849.912] -translate-y-1/2 overflow-hidden max-md:left-[2.344%] max-md:w-[31.12%] md:left-[1.302%] md:w-[31.034%]">
                    {/* All partner portraits are stacked; the active one fades in
                     *  while the rest fade out, so changing the selected partner
                     *  crossfades between banners instead of swapping instantly.
                     *  Respects prefers-reduced-motion. */}
                    {partners.map((partner, index) => {
                        const src = resolvePartnerImage(partner);
                        if (!src) {
                            return null;
                        }
                        const isActive = index === activeIndex;
                        return (
                            <img
                                key={`${partner.name}-${index}`}
                                src={src}
                                alt=""
                                aria-hidden={!isActive}
                                data-partners-image
                                className={cn(
                                    'absolute inset-0 size-full object-cover transition-opacity duration-500 ease-out motion-reduce:transition-none',
                                    isActive ? 'opacity-100' : 'opacity-0',
                                )}
                            />
                        );
                    })}
                </div>
            )}

            {/* "Meet our partners" label — desktop only. Mobile Figma omits it. */}
            <p className="absolute left-[34.39%] top-[10.018%] whitespace-nowrap text-[1.458vw] leading-[1.07] tracking-[-0.044em] max-md:hidden">
                {section.title}
            </p>

            {/* Up chevron.
             *  - Desktop: x=1119, y=21, size=30 → 58.28 % left / 2.57 % top.
             *  - Mobile (Figma): x=347, y=30, size=30 → 45.18 % left / 7.03 %
             *    top, size 30 px (≈ 7.8 vw of the 390 px mobile viewport). */}
            <button
                type="button"
                aria-label="Previous partner"
                onClick={onScrollUp}
                disabled={!canScrollUp}
                className={cn(
                    'absolute rotate-180 text-[#181818] transition-opacity duration-200',
                    'md:left-[58.284%] md:top-[2.566%] md:size-[1.578vw]',
                    // Mobile (Figma 521:26866): x=347.93/768=45.30 %,
                    // y=0.26/427=0.06 %, size=30.305/768=3.95 vw.
                    'max-md:left-[45.30%] max-md:top-[0.06%] max-md:size-[3.95vw]',
                    canScrollUp
                        ? 'cursor-pointer opacity-100'
                        : 'cursor-not-allowed opacity-50',
                )}
                data-partners-up
            >
                <ChevronDown className="size-full" />
            </button>

            {/* Name list window.
             *  - Desktop: x=1119, y=68, w=753 → 58.28 % left / 8.22 % top / 39.24 % wide.
             *  - Mobile (Figma): x=347, y=61, w=419 → 45.18 % left / 14.29 %
             *    top / 54.6 % wide. Font drops from 80 px to ~40 px (5.21 vw at 768 px). */}
            <div
                className="absolute overflow-hidden max-md:left-[45.18%] max-md:top-[14.29%] max-md:w-[54.6%] md:left-[58.284%] md:top-[8.215%] md:w-[39.244%]"
                style={
                    windowHeight !== null
                        ? { height: `${windowHeight}px` }
                        : undefined
                }
                aria-label="Partner list"
                onMouseLeave={onListMouseLeave}
            >
                <div
                    data-partners-scroll
                    className="flex flex-col leading-none tracking-[-0.048em] transition-transform duration-300 ease-out max-md:text-[5.21vw] md:text-[4.167vw]"
                    style={{ transform: `translateY(-${translateY}px)` }}
                >
                    {partners.map((partner, index) => {
                        const setRef = (node: HTMLElement | null) => {
                            itemRefs.current[index] = node;
                        };
                        // Hovering/focusing a name swaps the displayed banner.
                        const previewProps = {
                            onMouseEnter: () => setHoveredIndex(index),
                            onFocus: () => setHoveredIndex(index),
                            onBlur: () => setHoveredIndex(null),
                        };
                        const className = cn(
                            'block w-full cursor-pointer text-left transition-colors duration-200',
                            index === activeIndex
                                ? 'text-[#181818]'
                                : 'text-[#c4c4c4]',
                        );

                        // Desktop: the name links to the professional's profile,
                        // opening in a new tab; hover swaps the displayed banner.
                        // Mobile has no hover, so the name is a button — a tap
                        // switches the active banner instead of navigating.
                        if (partner.profileHref && !isMobile) {
                            return (
                                <a
                                    key={`${partner.name}-${index}`}
                                    ref={setRef}
                                    href={partner.profileHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={className}
                                    {...previewProps}
                                >
                                    {partner.name}
                                </a>
                            );
                        }

                        return (
                            <button
                                type="button"
                                key={`${partner.name}-${index}`}
                                ref={setRef}
                                onClick={() => setClickedIndex(index)}
                                className={className}
                                {...previewProps}
                            >
                                {partner.name}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Down chevron — symmetric to the up chevron. */}
            <button
                type="button"
                aria-label="Next partner"
                onClick={onScrollDown}
                disabled={!canScrollDown}
                className={cn(
                    'absolute text-[#181818] transition-opacity duration-200',
                    'md:left-[58.284%] md:top-[90.787%] md:size-[1.578vw]',
                    // Mobile (Figma 521:26866): x=347.93/768=45.30 %,
                    // y=396.73/427=92.91 %, size=30.305/768=3.95 vw.
                    'max-md:left-[45.30%] max-md:top-[92.91%] max-md:size-[3.95vw]',
                    canScrollDown
                        ? 'cursor-pointer opacity-100'
                        : 'cursor-not-allowed opacity-50',
                )}
                data-partners-down
            >
                <ChevronDown className="size-full" />
            </button>
        </section>
    );
};
